import React from 'react';
import { Box, Chip, ClickAwayListener, IconButton, Paper, Switch, TableCell, TableRow, Tooltip, useMediaQuery } from "@material-ui/core";
import { createStyles, makeStyles, Theme, useTheme } from "@material-ui/core/styles";

import KeyboardArrowUpRoundedIcon from '@material-ui/icons/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';

import { formatNumber } from "../../../helpers/helpers";


const useStyles = makeStyles((theme: Theme) => createStyles({
  td: {
    borderBottomWidth: 0,
  },
  chip: {
    backgroundColor: '#9AD0F8',
  },
  iconButton: {
    backgroundColor: '#000',
    color: '#FFF',
    '&:hover': {
      backgroundColor: '#222',
    },
  },
  mobileIconButton: {
    width: 48,
    height: 48,
    padding: 0,
  },
  mobileTapTarget: {
    minWidth: 48,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 10,
    objectFit: 'cover',
    display: 'block',
  },
  detailsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    columnGap: 30,
    padding: '0 42px 0 42px',
    '& p': {
      marginTop: 0,
      marginBottom: '1.25em',
    },
    '& p:has(+ ul)': {
      marginBottom: 0,
    },
    '& ul': {
      marginTop: 0,
      marginBottom: '1em',
      paddingLeft: '1.5em',
    },
    [theme.breakpoints.down(960)]: {
      flexDirection: 'column',
      columnGap: 0,
      rowGap: theme.spacing(1.5),
      padding: `${theme.spacing(1)}px 0 0`,
    },
  },
  detailsColumn: {
    flex: 1,
    color: 'rgba(50, 50, 50, 0.7)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  cardLast: {
    marginBottom: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  cardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 1.3,
  },
  cardDescription: {
    color: 'rgba(50, 50, 50, 0.7)',
  },
  cardCommission: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.5),
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardDetails: {
    paddingTop: theme.spacing(1),
  },
}));


interface Props {
  title: string;
  url: string;
  webSiteLabel: string;
  logo?: string;
  description: string;
  details1: string;
  details2: string;
  commissionRate: number;
  selected: boolean;
  specialConditions: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  disabled?: boolean;
  isLast?: boolean;
  layout?: 'table' | 'card';
}

/** @description Строка или карточка канала привлечения */
export function Channel({
  title,
  url,
  webSiteLabel,
  logo,
  description,
  details1,
  details2,
  commissionRate,
  selected,
  specialConditions,
  handleChange,
  disabled,
  isLast,
  layout = 'table',
}: Props) {
  const classes = useStyles();
  const [isDetailsOpen, setIsDetailsOpen] = React.useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(960));
  const isCardLayout = layout === 'card';

  const checkboxControl = isCardLayout ? (
    <Box className={classes.mobileTapTarget}>
      <Switch
        color={!!disabled ? undefined : "primary"}
        checked={selected}
        onChange={handleChange}
        disabled={!!disabled}
        size="small"
      />
    </Box>
  ) : (
    <Switch
      color={!!disabled ? undefined : "primary"}
      checked={selected}
      onChange={handleChange}
      disabled={!!disabled}
      size="small"
    />
  );
  const checkboxWithTooltip = disabled ? (
    <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
      <div>
        <Tooltip
          title="Примите оферту и&nbsp;заполните обязательные поля, чтобы подключить источник"
          open={isTooltipOpen}
          disableHoverListener={isMobile}
          disableFocusListener={isMobile}
          disableTouchListener={isMobile}
          onOpen={isMobile ? undefined : () => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <div
            onClick={isMobile ? () => setIsTooltipOpen((prev) => !prev) : undefined}
          >
            {checkboxControl}
          </div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  ) : checkboxControl;

  const detailsContent = (
    <Box className={classes.detailsContainer}>
      <Box className={classes.detailsColumn} dangerouslySetInnerHTML={{ __html: details1 }} />
      <Box className={classes.detailsColumn} dangerouslySetInnerHTML={{ __html: details2 }} />
    </Box>
  );

  if (isCardLayout) {
    const cardClassName = `${classes.card}${isLast ? ` ${classes.cardLast}` : ''}`;

    return (
      <Paper elevation={3} className={cardClassName}>
        <Box className={classes.cardHeader}>
          {logo && <img src={logo} alt="" className={classes.logo} />}
          <Box className={classes.cardHeaderText}>
            <Box className={classes.cardTitle}>{title}</Box>
            <a href={url} target="_blank" rel="noreferrer">{webSiteLabel}</a>
          </Box>
        </Box>
        <Box className={classes.cardDescription} dangerouslySetInnerHTML={{ __html: description }} />
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box className={classes.cardActions}>
              {checkboxWithTooltip}
            </Box>
            <Box className={classes.cardCommission}>
              <Box>{formatNumber(commissionRate)}% от чека</Box>
              {specialConditions && (
                <Chip
                  label="Индивидуальные условия"
                  className={classes.chip}
                  size="small"
                />
              )}
            </Box>

          </Box>
          {!isDetailsOpen && (
            <IconButton
              className={`${classes.iconButton} ${classes.mobileIconButton}`}
              onClick={() => setIsDetailsOpen(true)}
            >
              <KeyboardArrowDownRoundedIcon />
            </IconButton>
          )}
          {isDetailsOpen && (
            <IconButton
              className={`${classes.iconButton} ${classes.mobileIconButton}`}
              color="primary"
              onClick={() => setIsDetailsOpen(false)}
            >
              <KeyboardArrowUpRoundedIcon />
            </IconButton>
          )}
        </Box>
        {isDetailsOpen && (
          <Box className={classes.cardDetails}>
            {detailsContent}
          </Box>
        )}
      </Paper>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell component="th" scope="row" className={classes.td}>
          <Box display="flex" alignItems="center">
            {logo && <img src={logo} alt="" className={classes.logo} />}
            <Box>
              {title}<br />
              <a href={url} target="_blank" rel="noreferrer">{webSiteLabel}</a>
            </Box>
          </Box>
        </TableCell>
        <TableCell className={classes.td} dangerouslySetInnerHTML={{ __html: description }} />
        <TableCell className={classes.td}>
          {formatNumber(commissionRate)}% от чека{' '}
          {specialConditions && (
            <>
              <br />
              <Chip
                label="Индивидуальные условия"
                className={classes.chip}
                size="small"
              />
            </>
          )}
        </TableCell>
        <TableCell align="center" className={classes.td}>
          {checkboxWithTooltip}
        </TableCell>
        <TableCell align="center" className={classes.td}>
          {!isDetailsOpen && (
            <IconButton
              className={classes.iconButton}
              size="small"
              onClick={() => setIsDetailsOpen(true)}
            >
              <KeyboardArrowDownRoundedIcon />
            </IconButton>
          )}
          {isDetailsOpen && (
            <IconButton
              className={classes.iconButton}
              size="small"
              color="primary"
              onClick={() => setIsDetailsOpen(false)}
            >
              <KeyboardArrowUpRoundedIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {isDetailsOpen && (
        <TableRow>
          <TableCell colSpan={5} className={classes.td}>
            {detailsContent}
          </TableCell>
        </TableRow>
      )}
      {!isLast && <TableRow>
        <TableCell colSpan={5} style={{ height: '16px', padding: 0 }} />
      </TableRow>}
    </>
  );
}
